import { createContext, FC, ReactNode, useContext } from 'react';
import { PaulinaChat, usePaulinaChat } from './usePaulinaChat';
import { PaulinaShouldYield, usePaulinaShouldYield } from './usePaulinaShouldYield';

/**
 * PaulinaProvider — estado compartido de Paulina (Opción A).
 *
 * Decisión de diseño (Provider vs. elevar a Layout): se eligió un CONTEXTO porque
 * las dos superficies que abren el chat viven en subárboles HERMANOS de `Layout`,
 * no en una relación padre/hijo:
 *   - el avatar del HEADER móvil vive dentro de `MobileTopBarV4`,
 *   - la pill/panel (desktop) y el bottom-sheet (móvil) viven dentro de `AgentChatWidget`.
 * `usePaulinaChat()` es dueño del estado `open` y además dispara polling, fetch de
 * nudges, rage-click y telemetría; invocarlo en DOS lugares crearía dos instancias
 * independientes (doble polling, doble nudge, open-state divergente). El provider lo
 * llama UNA sola vez y comparte el resultado, así el header y el sheet abren el MISMO
 * chat. Elevar el estado a Layout obligaría a prop-drillear `open/setOpen/enabled`
 * dentro de `MobileTopBarV4` y a renderizar el avatar allí, acoplando el header a los
 * internos del chat; el contexto mantiene el header tonto (`usePaulina()` → lo que
 * necesita y nada más).
 *
 * Combina `usePaulinaChat` (lógica) con `usePaulinaShouldYield` (cesión ante pedidos)
 * en una sola señal para toda la UI de Paulina.
 */

export interface PaulinaContextValue extends PaulinaChat, PaulinaShouldYield {}

const PaulinaContext = createContext<PaulinaContextValue | null>(null);

export const PaulinaProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const chat = usePaulinaChat();
  const { hayPedido } = usePaulinaShouldYield();
  return <PaulinaContext.Provider value={{ ...chat, hayPedido }}>{children}</PaulinaContext.Provider>;
};

// Acceso al estado de Paulina. Devuelve `null` si se consume fuera del provider
// (p. ej. una superficie montada antes de envolver el árbol) — el consumidor decide
// no renderizar en ese caso, en vez de tirar y romper toda la pantalla.
export const usePaulina = (): PaulinaContextValue | null => useContext(PaulinaContext);
